"use client";

import { usePathname } from "next/navigation";
import { useCallback, useRef, useState } from "react";
import { LogoTree } from "@/components/brand/LogoTree";
import { brand, contactNavItem, mainNav } from "@/content/site";
import { MobileMenu } from "./MobileMenu";
import { TransitionLink } from "./TransitionLink";
import styles from "./SiteHeader.module.css";

// Navegación principal (guía 5): Investigación · Modelos · Productos · Empresa y, a la
// derecha, Contacto. Fija siempre. En celular los accesos directos no van acá: aparecen
// dentro del plano de las nubes del inicio (HomeFilm, bloque "nav").

export function SiteHeader() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback((refocus: boolean) => {
    setMenuOpen(false);
    if (refocus) toggleRef.current?.focus();
  }, []);

  const isActive = (href: string) => pathname === href || pathname.startsWith(href + "/");

  return (
    <header className={styles.header}>
      {/* data-egg-logo: 7 clics lo hacen florecer; mantenerlo apretado abre la terminal (easter eggs 3.1 y 2). */}
      <TransitionLink href="/" className={styles.brand} aria-label="YvyrAI, ir al inicio" data-egg-logo="">
        <LogoTree className={styles.logo} />
        <span className={styles.wordmark}>
          {brand.name.slice(0, -2)}
          <span className={styles.wordmarkAi}>{brand.name.slice(-2)}</span>
        </span>
      </TransitionLink>

      <nav className={styles.nav} aria-label="Principal">
        <ul className={styles.links}>
          {mainNav.map((item) => (
            <li key={item.href}>
              <TransitionLink
                href={item.href}
                className={styles.link}
                aria-current={isActive(item.href) ? "page" : undefined}
              >
                {item.label}
              </TransitionLink>
            </li>
          ))}
        </ul>
        {/* Buscador del sitio (Ctrl + K); lo abre src/components/easter/EasterRoot.tsx. */}
        <button type="button" className={styles.search} data-egg-open="search" aria-label="Buscar en el sitio" title="Buscar (Ctrl K)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6" />
            <path d="m15 15 4.5 4.5" />
          </svg>
        </button>
        <TransitionLink
          href={contactNavItem.href}
          className={styles.contact}
          aria-current={isActive(contactNavItem.href) ? "page" : undefined}
        >
          {contactNavItem.label}
        </TransitionLink>
      </nav>

      <button
        ref={toggleRef}
        type="button"
        className={styles.menuButton}
        aria-expanded={menuOpen}
        aria-controls="menu-movil"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span className={styles.menuLabel}>{menuOpen ? "Cerrar" : "Menú"}</span>
        <span className={styles.menuIcon} aria-hidden="true" data-open={menuOpen ? "true" : undefined} />
      </button>

      <MobileMenu open={menuOpen} onClose={closeMenu} isActive={isActive} />
    </header>
  );
}
